import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { cacheDailyVisits, getCachedVisits, isOnline } from '../services/offlineQueue';
import '../styles/common.css';

// ─── Shared helpers ──────────────────────────────────────────────────────────

const fmt = (n) => parseFloat(n || 0).toFixed(2);

const today = () => new Date().toISOString().split('T')[0];

const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const StatCard = ({ label, value, color, linkTo, linkText }) => (
  <div className="card" style={{ flex: '1 1 220px' }}>
    <div style={{ fontSize: '0.875rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>{label}</div>
    <div style={{ fontSize: '1.75rem', fontWeight: '600', color: color || '#2c3e50' }}>{value}</div>
    {linkTo && (
      <Link
        to={linkTo}
        className="btn btn-sm btn-primary"
        style={{ marginTop: '0.75rem', width: '100%', justifyContent: 'center' }}
      >
        {linkText || 'View'}
      </Link>
    )}
  </div>
);

// ─── TECHNICIAN DASHBOARD ────────────────────────────────────────────────────

const TechnicianDashboard = ({ user }) => {
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [weekJobs, setWeekJobs] = useState([]);
  const [monthStats, setMonthStats] = useState({ completed: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (isOnline()) {
        // Fetch today's jobs
        const todayStr = today();
        const [servicesRes, statsRes] = await Promise.all([
          apiClient.get(`/services?technician_id=${user.id}&status=scheduled`).catch(() => ({ services: [] })),
          apiClient.get(`/reports/services?technician_id=${user.id}&start_date=${startOfMonth()}&end_date=${todayStr}`).catch(() => ({ services: [] })),
        ]);

        const allScheduled = servicesRes.services || [];
        const todayJobs = allScheduled.filter((s) => s.scheduled_date === todayStr);
        const weekEnd = new Date();
        weekEnd.setDate(weekEnd.getDate() + 7);
        const weekEndStr = weekEnd.toISOString().split('T')[0];
        const upcoming = allScheduled.filter(
          (s) => s.scheduled_date >= todayStr && s.scheduled_date <= weekEndStr
        );

        setTodaysJobs(todayJobs);
        setWeekJobs(upcoming);

        // Cache today's visits for offline use
        await cacheDailyVisits(user.id, todayJobs);

        // Stats
        const servicesData = statsRes.services || statsRes.data || [];
        setMonthStats({
          completed: Array.isArray(servicesData) ? servicesData.length : 0,
        });
      } else {
        // Offline -- use cached data
        const cached = await getCachedVisits();
        setTodaysJobs(cached);
        setWeekJobs(cached);
        setMonthStats({ completed: 0 });
      }
    } catch (err) {
      console.error('Failed to load technician dashboard data:', err);
      // Try cached data on error
      try {
        const cached = await getCachedVisits();
        setTodaysJobs(cached);
      } catch {
        // Ignore
      }
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <StatCard label="Today's Jobs" value={todaysJobs.length} color="#667eea" />
        <StatCard label="This Week" value={weekJobs.length} color="#3498db" />
        <StatCard
          label="Completed This Month"
          value={monthStats.completed}
          color="#27ae60"
        />
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Quick Actions</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/services" className="btn btn-primary">
            Schedule Services
          </Link>
          <Link to="/customers/new" className="btn btn-primary">
            + Add Customer
          </Link>
        </div>
      </div>

      {/* Today's Jobs */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Today's Jobs</div>
        {todaysJobs.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <div className="empty-state-text">No jobs scheduled for today</div>
            <Link to="/services" className="btn btn-primary">
              View Schedule
            </Link>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Address</th>
                <th>Service Type</th>
                <th>Notes</th>
                <th>Cost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {todaysJobs.map((job) => (
                <tr key={job.id || job.visit_id}>
                  <td style={{ fontWeight: 500 }}>
                    {job.customer_name || `Customer #${job.customer_id}`}
                  </td>
                  <td style={{ fontSize: '0.875rem' }}>
                    {job.address || job.address_line1 || '-'}
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {job.service_type || job.service_type_name || '-'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.875rem', color: '#7f8c8d' }}>
                    {job.service_notes || job.notes || '-'}
                  </td>
                  <td>${fmt(job.final_cost)}</td>
                  <td>
                    <Link to="/services" className="btn btn-sm btn-success">
                      Complete
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* My Calendar (Week View) */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div className="card-header" style={{ marginBottom: 0 }}>My Calendar (This Week)</div>
          <Link to="/services" style={{ color: '#667eea', fontSize: '0.875rem' }}>
            View All
          </Link>
        </div>
        {weekJobs.length === 0 ? (
          <p style={{ color: '#7f8c8d' }}>No upcoming visits this week.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Customer</th>
                <th>Service Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {weekJobs.map((job) => (
                <tr key={job.id || job.visit_id}>
                  <td>
                    {job.scheduled_date
                      ? new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()
                      : '-'}
                  </td>
                  <td>{job.scheduled_time || '-'}</td>
                  <td>{job.customer_name || '-'}</td>
                  <td>
                    <span className="badge badge-info">
                      {job.service_type || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${job.status === 'completed' ? 'badge-success' : 'badge-info'}`}>
                      {job.status || 'scheduled'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── MANAGER DASHBOARD ───────────────────────────────────────────────────────

const ManagerDashboard = ({ user }) => {
  const [overview, setOverview] = useState({ overdue: 0, due: 0, upcoming: 0 });
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        const [dueRes, chemicalsRes] = await Promise.all([
          apiClient.get(`/agreements/due?month=${month}`).catch(() => ({ agreements: [] })),
          apiClient.get('/inventory/chemicals').catch(() => ({ chemicals: [] })),
        ]);

        const agreements = dueRes.agreements || [];
        setOverview({
          overdue: agreements.filter((a) => a.priority === 'overdue').length,
          due: agreements.filter((a) => a.priority === 'due').length,
          upcoming: agreements.filter((a) => a.priority === 'early').length,
        });

        // Find chemicals at or below reorder point (low stock = current_stock <= 5)
        const chemicals = chemicalsRes.chemicals || [];
        const lowStock = chemicals.filter((c) => parseFloat(c.current_stock || 0) <= 5);
        setInventoryAlerts(lowStock);
      } catch (err) {
        console.error('Failed to load manager dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Scheduling Overview */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Scheduling Overview</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <StatCard label="Overdue" value={overview.overdue} color="#e74c3c" />
          <StatCard label="Due Now" value={overview.due} color="#e67e22" />
          <StatCard label="Upcoming" value={overview.upcoming} color="#3498db" />
        </div>
      </div>

      {/* Inventory Alerts */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Inventory Alerts</div>
        {inventoryAlerts.length === 0 ? (
          <p style={{ color: '#27ae60' }}>All chemicals are adequately stocked.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Chemical</th>
                <th>Current Stock</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              {inventoryAlerts.map((chem) => (
                <tr key={chem.id}>
                  <td style={{ fontWeight: 500 }}>{chem.name}</td>
                  <td style={{ color: '#e74c3c', fontWeight: 600 }}>
                    {fmt(chem.current_stock)}
                  </td>
                  <td>{chem.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">Quick Actions</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/customers/new" className="btn btn-primary">
            + Add Customer
          </Link>
          <Link to="/inventory" className="btn btn-primary">
            Manage Inventory
          </Link>
          <Link to="/services" className="btn btn-primary">
            View Schedule
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── ADMIN / SUPERADMIN DASHBOARD ────────────────────────────────────────────

const AdminDashboard = ({ user }) => {
  const [overview, setOverview] = useState({ overdue: 0, due: 0, upcoming: 0 });
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [financials, setFinancials] = useState({ revenue: 0, expenses: 0, outstanding: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        const [dueRes, chemicalsRes, ledgerRes] = await Promise.all([
          apiClient.get(`/agreements/due?month=${month}`).catch(() => ({ agreements: [] })),
          apiClient.get('/inventory/chemicals').catch(() => ({ chemicals: [] })),
          apiClient.get('/ledger').catch(() => ({ entries: [] })),
        ]);

        // Scheduling overview
        const agreements = dueRes.agreements || [];
        setOverview({
          overdue: agreements.filter((a) => a.priority === 'overdue').length,
          due: agreements.filter((a) => a.priority === 'due').length,
          upcoming: agreements.filter((a) => a.priority === 'early').length,
        });

        // Inventory alerts
        const chemicals = chemicalsRes.chemicals || [];
        setInventoryAlerts(chemicals.filter((c) => parseFloat(c.current_stock || 0) <= 5));

        // Financial summary
        const entries = ledgerRes.entries || [];
        const totalCharges = entries
          .filter((e) => e.type === 'charge')
          .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
        const totalPayments = entries
          .filter((e) => e.type === 'payment')
          .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
        const totalExpenses = entries
          .filter((e) => e.type === 'expense' || e.type === 'employee_payment')
          .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

        setFinancials({
          revenue: totalCharges,
          expenses: totalExpenses,
          outstanding: totalCharges - totalPayments,
        });
      } catch (err) {
        console.error('Failed to load admin dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Financial Summary */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Financial Summary</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <StatCard label="Total Revenue" value={`$${fmt(financials.revenue)}`} color="#27ae60" />
          <StatCard label="Total Expenses" value={`$${fmt(financials.expenses)}`} color="#e67e22" />
          <StatCard label="Outstanding Balance" value={`$${fmt(financials.outstanding)}`} color="#e74c3c" />
        </div>
      </div>

      {/* Scheduling Overview */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Scheduling Overview</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <StatCard label="Overdue" value={overview.overdue} color="#e74c3c" />
          <StatCard label="Due Now" value={overview.due} color="#e67e22" />
          <StatCard label="Upcoming" value={overview.upcoming} color="#3498db" />
        </div>
      </div>

      {/* Inventory Alerts */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">Inventory Alerts</div>
        {inventoryAlerts.length === 0 ? (
          <p style={{ color: '#27ae60' }}>All chemicals are adequately stocked.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Chemical</th>
                <th>Current Stock</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              {inventoryAlerts.map((chem) => (
                <tr key={chem.id}>
                  <td style={{ fontWeight: 500 }}>{chem.name}</td>
                  <td style={{ color: '#e74c3c', fontWeight: 600 }}>{fmt(chem.current_stock)}</td>
                  <td>{chem.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">Quick Actions</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/customers/new" className="btn btn-primary">
            + Add Customer
          </Link>
          <Link to="/inventory" className="btn btn-primary">
            Manage Inventory
          </Link>
          <Link to="/users" className="btn btn-primary">
            Manage Users
          </Link>
          <Link to="/reports" className="btn btn-primary">
            View Reports
          </Link>
          <Link to="/payments" className="btn btn-primary">
            Financial Ledger
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user } = useAuth();

  const renderDashboard = () => {
    switch (user?.role) {
      case 'technician':
        return <TechnicianDashboard user={user} />;
      case 'manager':
        return <ManagerDashboard user={user} />;
      case 'admin':
      case 'superadmin':
        return <AdminDashboard user={user} />;
      default:
        return <TechnicianDashboard user={user} />;
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <div style={{ fontSize: '1rem', color: '#7f8c8d' }}>
          Welcome back, {user?.full_name || user?.username}!
          <span style={{ marginLeft: '0.5rem' }} className="badge badge-info">
            {user?.role}
          </span>
        </div>
      </div>
      {renderDashboard()}
    </div>
  );
};

export default Dashboard;
